import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Form } from 'antd';
import { useEffect } from 'react';
import { AUTH_SERVICES_BASE_URL, COMPANY_GROUPS_API_URL } from '@constants/url';
import useToken from '@hooks/useToken';
import { getFetch } from '@utils/get-fetch';
import { HttpError } from '@utils/request';

/** One object the principal is granted a role over. Repeatable in the form. */
export type PrincipalObject = {
	objectType: string;
	objectId: string;
	role: string;
};

/**
 * The authorization request the form collects. No `operationType`: this form
 * only ever assigns, and whoever submits it fills that in.
 */
export type PrincipalFormValues = {
	principalType: string;
	principal: string;
	objects: PrincipalObject[];
};

/** What the API accepts, which is broader than what this form collects. */
type PrincipalRequest = {
	principalType?: string;
	principal?: string;
	operationType?: string;
	objects?: Partial<PrincipalObject>[];
};

/** A dropdown choice: `key` is submitted, `value` is displayed. */
export type SelectOption = {
	key: string;
	value: string;
};

/**
 * What each principal type is allowed to assign, and over what. An omitted list
 * is no restriction rather than nothing allowed — the form offers every option
 * it knows.
 */
export const ALLOWED_BY_PRINCIPAL_TYPE: Record<
	string,
	{ roles?: string[]; objectTypes?: string[] }
> = {
	app: { roles: ['editor', 'viewer'], objectTypes: ['company', 'companyGroup'] },
	companyGroup: { roles: ['group'], objectTypes: ['company'] },
	user: { roles: ['admin', 'editor', 'viewer'], objectTypes: ['pcc', 'globalGroup'] },
};

/**
 * A field with exactly one legal value is filled in rather than asked for — the
 * same condition that renders it read-only. Used both when the principal type
 * changes and when a row is added, so new rows arrive pre-filled too.
 */
export const getObjectDefaults = (
	principalType: string | undefined
): Partial<PrincipalObject> => {
	const allowed = principalType ? ALLOWED_BY_PRINCIPAL_TYPE[principalType] : undefined;
	const defaults: Partial<PrincipalObject> = {};

	if (allowed?.roles?.length === 1) {
		defaults.role = allowed.roles[0];
	}

	if (allowed?.objectTypes?.length === 1) {
		defaults.objectType = allowed.objectTypes[0];
	}

	return defaults;
};

/** Rows the user has added but not filled in yet are partial. */
type PartialObject = Partial<PrincipalObject> | undefined;

export const toPrincipalPayload = (values: PrincipalRequest): Record<string, unknown> => ({
	principalType: values.principalType ?? '',
	principal: values.principal ?? '',
	operationType: values.operationType ?? '',
	objects: (values.objects ?? []).map((object: PartialObject) => ({
		objectType: object?.objectType ?? '',
		objectId: object?.objectId ?? '',
		role: object?.role ?? '',
	})),
});

/**
 * The endpoint returns a flat array of company group names — e.g.
 * `["all-companies-group", "test-1"]` — so each entry is both the submitted
 * value and the label. Anything that isn't a non-empty string is ignored.
 */
export const toCompanyGroupOptions = (data: unknown): SelectOption[] =>
	Array.isArray(data)
		? data
				.filter((item): item is string => typeof item === 'string' && !!item)
				.map((name) => ({ key: name, value: name }))
		: [];

type UseAssignmentFormOptions = {
	/**
	 * Where a valid form goes. Omit it when there is no endpoint yet: the fields
	 * and their validation still work, and `canSubmit` comes back false so the
	 * form can say why Submit is off.
	 */
	submit?: (values: PrincipalFormValues) => Promise<unknown>;
	successMessage?: string;
};

/**
 * The assignment form's own state: which fields are editable, what their options
 * are, and what happens on submit. Shared by every page that collects an
 * assignment, so those pages differ only in their title and where they post to.
 */
const useAssignmentForm = ({
	submit,
	successMessage = 'Assignment submitted',
}: UseAssignmentFormOptions = {}) => {
	const token = useToken();
	// Context-aware replacements for antd's static methods, themed by the
	// ConfigProvider in App.tsx. No extra provider needed — App.tsx already
	// wraps the tree in antd's App.
	const { message, modal } = App.useApp();

	// The form instance lives here so the hook can reset it once a submit lands.
	const [form] = Form.useForm<PrincipalFormValues>();

	// Drive which fields are editable and how. Undefined until the Form mounts.
	const principalType = Form.useWatch('principalType', form);
	const principal = Form.useWatch('principal', form);
	const objects: PartialObject[] = Form.useWatch('objects', form) ?? [];

	// Company groups feed two different fields: the principal itself for a
	// company group principal, and an object id when that object is a group.
	const needsCompanyGroups =
		principalType === 'companyGroup' ||
		objects.some((object) => object?.objectType === 'companyGroup');

	// No point starting a second object while the first is half finished.
	const canAddObject =
		objects.length > 0 &&
		objects.every(
			(object) =>
				!!object?.role?.trim() && !!object?.objectType?.trim() && !!object?.objectId?.trim()
		);

	const {
		data: companyGroups,
		isLoading: isLoadingCompanyGroups,
		error: companyGroupsError,
	} = useQuery({
		queryKey: ['companyGroups'],
		queryFn: () => getFetch<unknown>(token, COMPANY_GROUPS_API_URL),
		// Not fetched until something actually needs the list.
		enabled: needsCompanyGroups && !!token && !!AUTH_SERVICES_BASE_URL,
		// Don't retry auth failures (global default is retry: 3).
		retry: (failureCount, err) =>
			!(err instanceof HttpError && (err.status === 401 || err.status === 403)) &&
			failureCount < 3,
	});

	// useQuery has no onError in v5, so surface the failure here rather than
	// letting it show up as an empty dropdown.
	useEffect(() => {
		if (companyGroupsError) {
			message.error(`Could not load company groups: ${companyGroupsError.message}`);
		}
	}, [companyGroupsError, message]);

	const {
		mutate: submitAssignment,
		isPending: isSubmitting,
		data: result,
	} = useMutation({
		mutationFn: async (values: PrincipalFormValues) => {
			// Submit is disabled without one, so this only guards a caller that
			// reaches the mutation another way.
			if (!submit) {
				throw new Error('This form has nowhere to submit to yet');
			}

			return await submit(values);
		},
		onSuccess: () => {
			message.success(successMessage);
			form.resetFields();
		},
		onError: (error) => {
			message.error(error instanceof Error ? error.message : 'Request failed');
		},
	});

	/**
	 * Principal means a different thing per principal type — a client ID for an
	 * app, a group name for a company group — so it can't carry across. Nor can
	 * the objects: a company group principal only ever assigns the `group` role
	 * over a `company`, so the one row is filled in rather than asked for.
	 */
	const handleValuesChange = (changed: Partial<PrincipalFormValues>) => {
		if ('principalType' in changed) {
			form.setFieldsValue({
				principal: undefined,
				objects: [getObjectDefaults(changed.principalType)],
			} as Partial<PrincipalFormValues>);
		}
	};

	/**
	 * An object id's control changes with its own object type, so a value picked
	 * under the old type can't stand. Driven by the field's own onChange rather
	 * than onValuesChange: adding a row reports the whole `objects` array as
	 * changed, which would clear ids the user had already filled in.
	 */
	const clearObjectId = (index: number) => {
		form.setFieldValue(['objects', index, 'objectId'], undefined);
	};

	/**
	 * Whether the user has actually put anything into a row. Fields with a
	 * single legal value are pre-filled and rendered read-only, so they were
	 * never entered and don't count — the same set `getObjectDefaults` returns.
	 */
	const hasEnteredValues = (index: number): boolean => {
		const object = objects[index];
		const prefilled = getObjectDefaults(principalType);

		return (['role', 'objectType', 'objectId'] as const).some(
			(field) => !(field in prefilled) && !!object?.[field]?.trim()
		);
	};

	/**
	 * Removing a row discards whatever was entered into it, so it asks first —
	 * but only when there's something to lose. `onConfirm` comes from
	 * Form.List's own `remove`.
	 */
	const confirmRemoveObject = (index: number, onConfirm: () => void) => {
		if (!hasEnteredValues(index)) {
			onConfirm();

			return;
		}

		modal.confirm({
			title: `Remove object ${index + 1}?`,
			content: 'Its role, object type and id will be discarded.',
			okText: 'Remove',
			okButtonProps: { danger: true },
			cancelText: 'Keep',
			onOk: onConfirm,
		});
	};

	return {
		form,
		principalType,
		principal,
		objects,
		canAddObject,
		companyGroupOptions: toCompanyGroupOptions(companyGroups),
		isLoadingCompanyGroups,
		handleValuesChange,
		clearObjectId,
		confirmRemoveObject,
		submitAssignment,
		isSubmitting,
		result,
		canSubmit: !!submit,
	};
};

export type AssignmentFormState = ReturnType<typeof useAssignmentForm>;

export default useAssignmentForm;
